import { createContext, useContext, useState, useEffect } from "react";
import { ChevronsDownUp, ChevronsUpDown, ChevronDown, ChevronRight } from "lucide-react";

// Lets panels rendered inside a dashboard share its session collapse map.
export const CollapseContext = createContext(null);

export function useSectionCollapse(id) {
  const ctx = useContext(CollapseContext);
  const [local, setLocal] = useState(false);
  if (!ctx) return [local, () => setLocal((v) => !v)];
  return [!!ctx.collapsed[id], () => ctx.toggle(id)];
}

export function SectionChevron({ collapsed, onToggle, testId }) {
  return (
    <button type="button" onClick={onToggle} title={collapsed ? "Expand" : "Collapse"} aria-expanded={!collapsed} data-testid={testId}
      className="shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-200 transition-colors">
      {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </button>
  );
}

// Collapsed-section map persisted for the browser session (default: everything expanded).
export function useSessionCollapse(storageKey) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(storageKey) || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(storageKey, JSON.stringify(collapsed)); } catch { /* private mode */ }
  }, [storageKey, collapsed]);
  const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  const setAll = (keys, value) => setCollapsed(Object.fromEntries(keys.map((k) => [k, value])));
  return [collapsed, toggle, setAll];
}

// Icon-only global expand/collapse toggle.
export default function ExpandCollapseAll({ allCollapsed, onToggle, testId = "expand-collapse-all" }) {
  const Icon = allCollapsed ? ChevronsUpDown : ChevronsDownUp;
  const label = allCollapsed ? "Expand all" : "Collapse all";
  return (
    <button type="button" onClick={onToggle} title={label} aria-label={label} data-testid={testId}
      className="seg-rail inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:text-foreground">
      <Icon className="h-4 w-4" />
    </button>
  );
}
